import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { Row, Col, Button } from 'reactstrap';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import { displayError } from '../../helpers';
import ErrorToast from '../../ErrorToast';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import {
    cancelPurchaseCreditNote,
    deletePurchaseCreditNote,
    getPurchaseCreditNoteDetails,
} from '../../services/PurchaseCreditNoteServices';
import { fetchAndShowPurchaseCreditNotePdf } from '../../utils/purchaseCreditNotePdf';
import {
    buildPurchaseCreditNoteTotals,
    getPurchaseCreditNoteTypeLabel,
} from './PurchaseCreditNoteUtils';
import TableRowsPurchaseCreditNote from './TableRowsPurchaseCreditNote';
import moment from 'moment';

function PurchaseCreditNoteEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [inputs, setInputs] = useState({});
    const [productLines, setProductLines] = useState([]);
    const [documentLines, setDocumentLines] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const isCancelled = inputs.status === false || inputs.status === 0 || inputs.status === 'Cancelled';

    const totals = useMemo(
        () => buildPurchaseCreditNoteTotals(productLines, documentLines),
        [productLines, documentLines],
    );

    useEffect(() => {
        setInProgress(true);
        getPurchaseCreditNoteDetails(cookies, id)
            .then((response) => {
                setInputs({
                    ...response,
                    cn_date: moment(response.cn_date, 'DD-MM-YYYY').format('YYYY-MM-DD'),
                    cn_no: response.cn_no,
                });
                setProductLines(response.product_lines || []);
                setDocumentLines((response.document_lines || []).map((line) => ({
                    ...line,
                    doc_type: 'GRN',
                    reference_no: response.reference_no,
                    credit_amt: line.credit_amt,
                })));
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load purchase credit note.' }, 'Load Failed');
            })
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const onPrint = () => {
        setInProgress(true);
        fetchAndShowPurchaseCreditNotePdf(cookies, id, { title: 'Purchase Credit Note PDF' })
            .finally(() => setInProgress(false));
    };

    const onCancel = () => {
        Swal.fire({
            title: 'Cancel this purchase credit note?',
            text: 'Mark as inactive? Values are retained for audit.',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            cancelPurchaseCreditNote(cookies, id)
                .then((response) => {
                    Swal.fire(response.message || 'Cancelled!', '', 'success');
                    navigate('/PurchaseCreditNoteTable');
                })
                .catch((error) => {
                    displayError(error?.response?.data || { message: error?.message }, 'Cancel Failed');
                })
                .finally(() => setInProgress(false));
        });
    };

    const onDelete = () => {
        Swal.fire({
            title: 'Delete this cancelled purchase credit note?',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'No',
        }).then((result) => {
            if (!result.isConfirmed) {
                return;
            }
            setInProgress(true);
            deletePurchaseCreditNote(cookies, id)
                .then(() => {
                    Swal.fire('Deleted!', '', 'success');
                    navigate('/PurchaseCreditNoteTable');
                })
                .catch((error) => {
                    displayError(error?.response?.data || { message: error?.message }, 'Delete Failed');
                })
                .finally(() => setInProgress(false));
        });
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay inProgress={inProgress} />
            <div id="page-wrap">
                <form>
                    <FloatingControls tableLink="/PurchaseCreditNoteTable" formLink="/PurchaseCreditNote" addNewLabel="Add New" enableCancel={false} />
                    <FormPageLayout
                        moduleLabel="PURCHASE"
                        title="Update Purchase Credit Note"
                        subtitle="Review vendor credit note against GRN for purchase return or adjustments."
                        modeLabel={`${getPurchaseCreditNoteTypeLabel(inputs.cn_type).toUpperCase()} UPDATE MODE`}
                        headerExtra={isCancelled ? (
                            <div className="alert alert-warning mb-0">This purchase credit note is inactive. Values are retained for audit only.</div>
                        ) : null}
                    >
                        <div className="container form-control-panel">
                            <Row>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-5 text-right">Plant</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.plant_alias || inputs.plant_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Vendor</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.vendor_name || inputs.vendor_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">GRN Reference</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.reference_no || inputs.gmr_id || ''} /><br />
                                        <label className="form-group col-sm-5 text-right">Credit Note Type</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.cn_type_label || getPurchaseCreditNoteTypeLabel(inputs.cn_type)} /><br />
                                        <label className="form-group col-sm-5 text-right">Voucher Mode</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.voucher_mode || ''} />
                                    </div>
                                </Col>
                                <Col xl={6} lg={12} md={12}>
                                    <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                        <label className="form-group col-sm-4 text-right">Credit Note No.</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.cn_no || ''} /><br />
                                        <label className="form-group col-sm-4 text-right">Credit Note Date</label>
                                        <input className="form-control col-sm-6" readOnly value={inputs.cn_date || ''} /><br />
                                        <label className="form-group col-sm-4 text-right">Status</label>
                                        <input className="form-control col-sm-6" readOnly value={isCancelled ? 'Cancelled' : 'Active'} />
                                    </div>
                                </Col>
                            </Row>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Action</th>
                                            <th>S.No</th>
                                            <th>Description</th>
                                            <th>Unit</th>
                                            <th>Qty</th>
                                            <th>Basic Rate</th>
                                            <th>Amount</th>
                                            <th>Tax %</th>
                                            <th>Tax Amount</th>
                                            <th>Total Amount</th>
                                        </tr>
                                    </thead>
                                    <TableRowsPurchaseCreditNote rowsData={productLines} taxes={[]} cnType={inputs.cn_type} readOnly />
                                </table>
                            </div>

                            <div className="form-row shadow p-2 my-2 border-secondary p-2 mb-3 container table-responsive">
                                <table className="table-bordered searchTable table order-list" style={{ textAlign: 'center' }}>
                                    <thead>
                                        <tr>
                                            <th>Sl. No.</th>
                                            <th>Doc Type</th>
                                            <th>Reference No</th>
                                            <th>Credit Amount</th>
                                            <th>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {documentLines.map((line, index) => (
                                            <tr key={`pcn-edit-doc-${index}`}>
                                                <td>{index + 1}</td>
                                                <td>{line.doc_type || 'GRN'}</td>
                                                <td>{line.reference_no || inputs.reference_no}</td>
                                                <td>{line.credit_amt}</td>
                                                <td>{line.remarks || ''}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label className="form-group col-sm-2 text-right">Remarks</label>
                                <textarea rows="2" className="form-control col-sm-10" readOnly value={inputs.remarks || ''} />
                            </div>

                            <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                <label className="form-group col-sm-3 text-right">Total Credit Note Value</label>
                                <input className="form-control col-sm-3" readOnly value={totals.credit_amt || inputs.credit_amt || 0} />
                            </div>

                            {inputs.accounting_hint && (
                                <div className="form-row table-bordered shadow p-2 my-2 border-secondary p-2 mb-3 form-control-panel">
                                    <label className="form-group col-sm-2 text-right">Accounting</label>
                                    <div className="col-sm-10 small text-muted">
                                        <div>{inputs.accounting_hint.party_ledger} · {inputs.accounting_hint.expense_ledger} · {inputs.accounting_hint.gst_ledger}</div>
                                        <div>{inputs.accounting_hint.narration}</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="footer company-form-footer text-center">
                            <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={onPrint}><i className="fa fa-print" aria-hidden="true" /> Print</Button>
                            <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/PurchaseCreditNoteTable')}><i className="fa fa-eye" aria-hidden="true" /> View</Button>
                            <Button type="button" className="btn btn-twitter company-form-action-btn" style={{ width: '80px', fontWeight: 'bold' }} onClick={() => navigate('/Home')}><i className="fa fa-home" aria-hidden="true" /> Home</Button>
                            {!isCancelled && (
                                <Button type="button" className="btn btn-cancel" style={{ width: '120px', fontWeight: 'bold', marginLeft: '8px' }} onClick={onCancel}>Cancel PCN</Button>
                            )}
                            {isCancelled && (
                                <Button type="button" className="btn btn-delete" style={{ width: '80px', fontWeight: 'bold', marginLeft: '8px' }} onClick={onDelete}>Delete</Button>
                            )}
                        </div>
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default PurchaseCreditNoteEdit;
